/**
 * CopilotPanel — AI Operations Copilot (Phase 1, admin-only, READ-ONLY).
 *
 * On-demand plain-English summaries of existing operational data
 * (operations, ride health, fatigue, compliance, KPIs, corporate) generated by
 * Claude via POST /api/admin/copilot/summarize. Advisory only — it reads
 * already-aggregated reporting data and never changes any dispatch / lifecycle
 * / pricing / refund state.
 */
import React, { useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Bot, Sparkles, RefreshCw, ShieldCheck, Activity, HeartPulse, Gauge,
  Building2, LayoutDashboard, AlertCircle,
} from "lucide-react";

const TOPICS = [
  { id: "operations",  label: "Operations Overview", icon: Activity,
    blurb: "Fleet status, availability & pressure points" },
  { id: "ride_health", label: "Ride Health", icon: HeartPulse,
    blurb: "Stuck rides, recovery & disconnects" },
  { id: "fatigue",     label: "Driver Fatigue", icon: Gauge,
    blurb: "Drivers approaching / over hour limits" },
  { id: "compliance",  label: "Compliance", icon: ShieldCheck,
    blurb: "Expiring & expired documents to chase" },
  { id: "kpis",        label: "KPI Dashboard", icon: LayoutDashboard,
    blurb: "Revenue, volume & utilisation trends" },
  { id: "corporate",   label: "Corporate Accounts", icon: Building2,
    blurb: "Account activity & outstanding receivables" },
];

const RANGES = [
  { id: "today", label: "Today" },
  { id: "wtd", label: "Week to date" },
  { id: "yesterday", label: "Yesterday" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "mtd", label: "Month to date" },
];

export const CopilotPanel = () => {
  const [active, setActive] = useState(null);
  const [range, setRange] = useState("today");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const generate = useCallback(async (topicId) => {
    setActive(topicId);
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const body = { topic: topicId };
      if (topicId === "kpis") body.range = range;
      const { data } = await api.post("/admin/copilot/summarize", body);
      setResult(data);
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      toast.error(`AI summary failed: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [range]);

  return (
    <div className="space-y-4" data-testid="copilot-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 grid place-items-center bg-[#0b1f3a] text-white rounded-md">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-black tracking-tight">AI Operations Copilot</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              On-demand AI summaries of your live operations data.
            </p>
          </div>
        </div>
        <span
          className="label-mini flex items-center gap-1.5 px-2.5 py-1 rounded-full"
          style={{ color: "#065F46", background: "#D1FAE5" }}
          data-testid="copilot-readonly-badge"
        >
          <ShieldCheck className="w-3.5 h-3.5" /> Read-only · Advisory
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {TOPICS.map((t) => {
          const Icon = t.icon;
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              onClick={() => generate(t.id)}
              disabled={loading}
              data-testid={`copilot-topic-${t.id}`}
              className={`text-left p-3 border rounded-md transition-colors disabled:opacity-60 ${
                isActive ? "border-[#0b1f3a] bg-[#0b1f3a]/[0.04]" : "border-border hover:border-[#0b1f3a]/40 bg-card"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4 text-[#0b1f3a]" />
                <span className="font-semibold text-sm">{t.label}</span>
              </div>
              <p className="text-[11px] text-muted-foreground">{t.blurb}</p>
            </button>
          );
        })}
      </div>

      {active === "kpis" && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="copilot-range-row">
          <span className="label-mini text-muted-foreground">Range:</span>
          {RANGES.map((r) => (
            <button
              key={r.id}
              onClick={() => setRange(r.id)}
              data-testid={`copilot-range-${r.id}`}
              className={`px-2.5 py-1 text-xs rounded-sm border transition-colors ${
                range === r.id ? "bg-[#0b1f3a] text-white border-[#0b1f3a]" : "border-border hover:border-[#0b1f3a]/40"
              }`}
            >
              {r.label}
            </button>
          ))}
          <button
            onClick={() => generate("kpis")}
            disabled={loading}
            className="ml-1 px-2.5 py-1 text-xs rounded-sm bg-[#0b1f3a] text-white flex items-center gap-1.5 disabled:opacity-60"
            data-testid="copilot-regenerate-kpis"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Regenerate
          </button>
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground p-4 border border-dashed rounded-md" data-testid="copilot-loading">
          <Sparkles className="w-4 h-4 animate-pulse text-[#0b1f3a]" />
          Analysing operations data…
        </div>
      )}

      {error && !loading && (
        <div className="flex items-start gap-2 p-3 border border-[#FCA5A5] bg-[#FEF2F2] rounded-md text-sm text-[#991B1B]" data-testid="copilot-error">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>{error}</div>
        </div>
      )}

      {result && !loading && (
        <div className="border border-border rounded-md bg-card" data-testid="copilot-result">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted/40">
            <span className="font-semibold text-sm">{result.label}</span>
            <button
              onClick={() => generate(result.topic)}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              data-testid="copilot-refresh-btn"
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
          <div
            className="px-4 py-3 prose prose-sm max-w-none prose-headings:font-bold prose-p:my-1.5 prose-ul:my-1.5 prose-li:my-0.5"
            data-testid="copilot-summary-text"
          >
            <ReactMarkdown>{result.summary}</ReactMarkdown>
          </div>
          <div className="px-4 py-2 border-t border-border text-[10px] text-muted-foreground flex items-center justify-between">
            <span>Generated by {result.model}</span>
            <span>{new Date(result.generated_at).toLocaleString("en-GB")}</span>
          </div>
        </div>
      )}

      {!active && !loading && (
        <p className="text-xs text-muted-foreground" data-testid="copilot-hint">
          Select a topic above to generate an AI summary. Summaries are advisory and read directly from your live operations data — nothing is changed.
        </p>
      )}
    </div>
  );
};

export default CopilotPanel;
